import { bootstrapDatabase, bootstrapTokens, syncDataWithDatabase } from '../bootstrap'
import { boostrapRootEpic } from '../bootstrapEpics'
import { createEpicMiddleware } from 'redux-observable'
import { applyMiddleware, createStore } from 'redux'
import { getRxStorageMemory } from 'rxdb/plugins/storage-memory'
import { CollectionsOfDatabase, RxDatabase, RxError } from 'rxdb'
import { IDbUtils } from '@obeta/models/lib/models/Db'
import { useEffect, useState } from 'react'
import { getCollectionSync } from '../bootstrap'
import { NormalizedCacheObject, ApolloClient } from '@apollo/client'
import { hasWindow } from '@obeta/utils/lib/ssr'
import { initAxiosShop } from '../axiosClient'
import { isAuthenticationErrorSync } from '@obeta/utils/lib/isAuthenticationError'
import { initAppActions } from '../bootstrapAppActions'
import { AppActions } from '@obeta/models/lib/models/BusinessLayer/AppActions'
import { initApollo } from '../apolloClient'
import { SecureStoragePlugin } from '@atroo/capacitor-secure-storage-plugin'
import { firstValueFrom, Subject } from 'rxjs'
import { GraphQLError } from 'graphql'
import { datadogRum } from '@datadog/browser-rum'
import { bootstrapBusinessLogic } from '../businessLayer'
import { BusinessLayer } from '@obeta/models/lib/models/BusinessLayer/BusinessLayer'
import { LOGIN_FORWARD_PARAM, options } from '@obeta/data/lib/hooks'
import { prepareAuthorizationProcess } from '@obeta/data/lib/hooks/oath2/useOauth2Web'
import { isCrawler } from '@obeta/utils/lib/is-crawler'

/**
 * Creates a function that returns a memory-based storage adapter if the current environment is detected as a crawler.
 *
 * @returns A function that returns a memory storage adapter if running in a crawler environment, otherwise `undefined`, which will use the default adapter.
 *
 * @remarks
 * This is useful for environments where persistent storage is not required or not available, such as server-side rendering or web crawlers.
 *
 * @see {@link isCrawler} for crawler detection logic.
 * @see {@link getRxStorageMemory} for the memory storage adapter implementation.
 */
function makeGetStorageAdapter() {
  if (isCrawler()) {
    return () => getRxStorageMemory()
  }

  return undefined
}

export const useShopBootstrap = (
  multiInstance: boolean,
  graphqlUrl?: string,
  ignoreDuplicate?: boolean
) => {
  const [db, setDb] = useState<RxDatabase<CollectionsOfDatabase> | null>(null)
  const [dbUtils, setDbUtils] = useState<IDbUtils | null>(null)
  const [store, setStore] = useState<ReturnType<typeof createStore> | null>(null)
  const [appActions, setAppActions] = useState<AppActions | null>(null)
  const [apolloClient, setApolloClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null)
  const [businessLayer, setBusinessLayer] = useState<BusinessLayer>({} as BusinessLayer)

  useEffect(() => {
    if (!hasWindow()) {
      return
    }

    const epicMiddleware = createEpicMiddleware()
    const store = createStore(() => {
      //
    }, applyMiddleware(epicMiddleware))
    setStore(store)

    const dbPromise = bootstrapDatabase(
      multiInstance,
      ignoreDuplicate,
      undefined,
      undefined,
      makeGetStorageAdapter()
    )
    const tokensPromise = bootstrapTokens()

    Promise.all([dbPromise, tokensPromise]).then(async ([dbInst, tokens]) => {
      /*
        if users get forwarded when logging in on the wrong domain,
        we need to make sure that they do not end up with user data of a former session
        - for simplicities sake we do not differentiate if the logged in user was the user themself already
       */
      if (window.location.search.includes(LOGIN_FORWARD_PARAM)) {
        if (tokens) {
          //1. clear secure storage
          await SecureStoragePlugin.remove({ key: 'auth' }) //make sure old auth data is forgotten
          //2. init app actions without token
          const actions = initAppActions(dbInst, null, store)
          //3. logout
          actions.logout$.next(true)
          await firstValueFrom(actions.logoutFinished$)
        }

        const { authorizationUrl, webOptions } = await prepareAuthorizationProcess(options)
        window.open(authorizationUrl, webOptions.windowTarget, webOptions.windowOptions)

        return
      }
      // TODO: batch state
      const userDoc = await dbInst.getLocal('userv2')
      if (!userDoc) {
        await dbInst.insertLocal('userv2', {})
      }
      const actions = initAppActions(dbInst, tokens, store)
      setAppActions(actions)
      const apollo = initApollo(actions, dbInst)

      const errorHandler = (err: RxError) => {
        if (!Array.isArray(err?.parameters?.errors)) {
          datadogRum.addError(err)
          return
        }

        const accessDeniedError = err.parameters.errors.find((errObj) => {
          /** errObj can actually be a GraphQLError */
          return isAuthenticationErrorSync(errObj as unknown as GraphQLError)
        })
        if (accessDeniedError) {
          actions.requestNewToken()
        } else {
          datadogRum.addError(err)
        }
      }

      const businessLayer = await bootstrapBusinessLogic(actions, dbInst, apollo, {
        baseUrl: graphqlUrl,
        token: tokens?.accessToken,
        entities: [
          'cartsv2',
          'carttemplates',
          'storesv2',
          'offersv2',
          'useraddressesv2',
          'openpositions',
        ],
        errorHandler,
      })
      setBusinessLayer(businessLayer)

      setApolloClient(apollo)

      setDb(dbInst)

      actions.tokens$.subscribe((tokens) => {
        if (tokens) {
          businessLayer.startSync()
        }
      })

      const { rootEpic, epic$ } = boostrapRootEpic(dbInst, apollo, getCollectionSync)
      const dbUtils = {
        epicInit: epic$,
        syncDataWithDatabase,
        getCollectionSync,
      }
      epicMiddleware.run(rootEpic)
      setDbUtils(dbUtils)
      const maintenanceModeSubject: Subject<string> = new Subject()
      initAxiosShop(dbInst, maintenanceModeSubject, actions)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiInstance, ignoreDuplicate, graphqlUrl])

  return {
    db,
    dbUtils,
    store,
    appActions,
    apolloClient,
    businessLayer,
  }
}
